import React, { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { ShoppingCart, Heart, Menu, X } from 'lucide-react';
import { useApp } from '../../contexts/AppContext';

const Header = () => {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const { getCartCount, wishlist } = useApp();
  const location = useLocation();

  const navigation = [
    { name: 'Home', path: '/' },
    { name: 'Shop', path: '/shop' },
    { name: 'About', path: '/about' },
    { name: 'Blog', path: '/blog' },
    { name: 'Contact', path: '/contact' }
  ];

  const isActive = (path) => location.pathname === path;

  return (
    <header className="sticky top-0 z-50">
      {/* Top Banner */}
      <div className="bg-[#F5EFE6] text-[#7A6B5D] text-center py-2 px-4 border-b border-[#E8DCC8]">
        <p className="text-[10px] sm:text-xs tracking-[0.2em] sm:tracking-[0.3em] uppercase font-light whitespace-nowrap overflow-hidden text-ellipsis">
          Handcrafted Luxury &middot; Free Shipping on Orders Above &#8377;15,000
        </p>
      </div>

      {/* Main Header */}
      <div className="bg-[#FAF7F2] border-b border-[#E8DCC8] shadow-sm">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16 sm:h-20 lg:h-24">
            {/* Logo */}
            <Link to="/" className="flex items-center space-x-2 sm:space-x-3 flex-shrink-0">
              <img 
                src="https://customer-assets.emergentagent.com/job_behad-living/artifacts/rxtdz05y_Behat_Atelier.png"
                alt="Behat House Logo"
                className="h-12 sm:h-14 lg:h-20 w-auto object-contain"
              />
              <div>
                <h1 className="text-base sm:text-lg lg:text-2xl font-serif text-black tracking-wide leading-tight">Behat House</h1>
                <p className="text-[8px] sm:text-[10px] lg:text-xs text-black tracking-[0.2em] sm:tracking-[0.35em] font-semibold uppercase font-serif">Atelier</p>
              </div>
            </Link>

            {/* Desktop Navigation */}
            <nav className="hidden lg:flex items-center space-x-10 xl:space-x-12">
              {navigation.map((item) => (
                <Link
                  key={item.name}
                  to={item.path}
                  className={`relative text-sm tracking-[0.15em] uppercase font-medium transition-all duration-300 ${
                    isActive(item.path)
                      ? 'text-[#3E2723]'
                      : 'text-[#7A6B5D] hover:text-[#3E2723]'
                  }`}
                >
                  {item.name}
                  {isActive(item.path) && (
                    <span className="absolute -bottom-1 left-0 right-0 h-0.5 bg-[#C4A265]"></span>
                  )}
                </Link>
              ))}
            </nav>

            {/* Icons */}
            <div className="flex items-center space-x-3 sm:space-x-5">
              <Link
                to="/wishlist"
                className="relative p-1.5 sm:p-2 text-[#7A6B5D] hover:text-[#3E2723] transition-colors duration-300"
                data-testid="wishlist-link"
              >
                <Heart className="w-5 h-5" strokeWidth={1.5} />
                {wishlist.product_ids.length > 0 && (
                  <span className="absolute -top-0.5 -right-0.5 sm:-top-1 sm:-right-1 bg-[#B8963E] text-white text-[9px] sm:text-[10px] rounded-full w-4 h-4 flex items-center justify-center font-medium">
                    {wishlist.product_ids.length}
                  </span>
                )}
              </Link>

              <Link
                to="/cart"
                className="relative p-1.5 sm:p-2 text-[#7A6B5D] hover:text-[#3E2723] transition-colors duration-300"
                data-testid="cart-link"
              >
                <ShoppingCart className="w-5 h-5" strokeWidth={1.5} />
                {getCartCount() > 0 && (
                  <span className="absolute -top-0.5 -right-0.5 sm:-top-1 sm:-right-1 bg-[#3E2723] text-white text-[9px] sm:text-[10px] rounded-full w-4 h-4 flex items-center justify-center font-medium">
                    {getCartCount()}
                  </span>
                )}
              </Link>

              {/* Mobile/Tablet menu button */}
              <button
                className="lg:hidden p-1.5 sm:p-2 text-[#7A6B5D] hover:text-[#3E2723] transition-colors"
                onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
                data-testid="mobile-menu-button"
              >
                {mobileMenuOpen ? (
                  <X className="w-5 h-5 sm:w-6 sm:h-6" strokeWidth={1.5} />
                ) : (
                  <Menu className="w-5 h-5 sm:w-6 sm:h-6" strokeWidth={1.5} />
                )}
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Mobile/Tablet Navigation */}
      {mobileMenuOpen && (
        <div className="lg:hidden bg-[#FAF7F2] border-b border-[#E8DCC8]">
          <div className="px-6 py-4 space-y-1">
            {navigation.map((item) => (
              <Link
                key={item.name}
                to={item.path}
                className={`block py-3 text-sm tracking-[0.15em] uppercase font-medium border-b border-[#F0EBE0] last:border-0 ${
                  isActive(item.path)
                    ? 'text-[#3E2723]'
                    : 'text-[#7A6B5D]'
                }`}
                onClick={() => setMobileMenuOpen(false)}
              >
                {item.name}
              </Link>
            ))}
          </div>
        </div>
      )}
    </header>
  );
};

export default Header;
